// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Upload } from 'lucide-react';
import { AffiliateProfile } from '@/hooks/useAffiliatePromoAssets';

interface UploadPromoAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, name: string, assignedToAll: boolean, assignedAffiliateIds: string[], displayType: string) => Promise<boolean>;
  affiliates: AffiliateProfile[];
}

const displayTypeOptions = [
  { value: 'Web - Horizontal Banner', label: 'Web - Horizontal Banner (728×90 or 970×250)' },
  { value: 'Web - Sidebar', label: 'Web - Sidebar (300×250 or 300×600)' },
  { value: 'Web - Inline / Medium', label: 'Web - Inline / Medium (468×60 or 320×100)' },
  { value: 'Social - Instagram Post', label: 'Social - Instagram Post (1080×1080)' },
  { value: 'Social - Instagram Story', label: 'Social - Instagram Story (1080×1920)' },
  { value: 'Social - Facebook Post', label: 'Social - Facebook Post (1200×628)' },
  { value: 'Social - Twitter Post', label: 'Social - Twitter Post (1200×675)' },
  { value: 'Social - LinkedIn Post', label: 'Social - LinkedIn Post (1200×627)' },
  { value: 'Print - Flyer Full Page', label: 'Print - Flyer Full Page (2550×3300 px or 8.5×11 in)' },
  { value: 'Print - Half Page', label: 'Print - Half Page (8.5×5.5 in)' },
  { value: 'Print - Business Card', label: 'Print - Business Card (3.5×2 in)' },
  { value: 'Email Header', label: 'Email Header (600×200 or 600×300)' },
  { value: 'SMS Thumbnail', label: 'SMS Thumbnail (640×640 or 500×500)' },
  { value: 'Messaging Preview', label: 'Messaging Preview (400×300)' },
  { value: 'QR Promo Card', label: 'QR Promo Card (square or vertical)' },
  { value: 'Sticker / Badge Format', label: 'Sticker / Badge Format (logo or compact square)' }
];

const UploadPromoAssetModal: React.FC<UploadPromoAssetModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  affiliates
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [displayType, setDisplayType] = useState('');
  const [assignedToAll, setAssignedToAll] = useState(true);
  const [selectedAffiliateIds, setSelectedAffiliateIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('Please select a JPG, PNG, or WebP image file.');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleAffiliateToggle = (affiliateId: string, checked: boolean) => {
    if (checked) {
      setSelectedAffiliateIds(prev => [...prev, affiliateId]);
    } else {
      setSelectedAffiliateIds(prev => prev.filter(id => id !== affiliateId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file || !name.trim() || !displayType) {
      alert('Please fill in all required fields: file, name, and display type.');
      return;
    }

    if (!assignedToAll && selectedAffiliateIds.length === 0) {
      alert('Please select at least one affiliate or assign to all affiliates.');
      return;
    }

    setUploading(true);
    const success = await onUpload(file, name.trim(), assignedToAll, selectedAffiliateIds, displayType);
    setUploading(false);

    if (success) {
      setFile(null);
      setName('');
      setDisplayType('');
      setAssignedToAll(true);
      setSelectedAffiliateIds([]);
      onClose();
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setName('');
      setDisplayType('');
      setAssignedToAll(true);
      setSelectedAffiliateIds([]);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Promo Asset</DialogTitle>
          <DialogDescription>
            Upload a promotional image for affiliates to use in their marketing
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="asset-file">Image File *</Label>
            <Input
              id="asset-file"
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              onChange={handleFileChange}
              disabled={uploading}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: JPG, PNG, WebP (max 10MB)
            </p>
          </div>

          <div>
            <Label htmlFor="asset-name">Asset Name *</Label>
            <Input
              id="asset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Instagram Square - June"
              disabled={uploading}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="display-type">Display Type *</Label>
            <Select value={displayType} onValueChange={setDisplayType} disabled={uploading}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select display type and size..." />
              </SelectTrigger>
              <SelectContent>
                {displayTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Switch
                id="assign-all"
                checked={assignedToAll}
                onCheckedChange={setAssignedToAll}
                disabled={uploading}
              />
              <Label htmlFor="assign-all">Assign to All Affiliates</Label>
            </div>

            {!assignedToAll && (
              <div>
                <Label>Select Specific Affiliates</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded p-2">
                  {affiliates.map((affiliate) => (
                    <div key={affiliate.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`affiliate-${affiliate.id}`}
                        checked={selectedAffiliateIds.includes(affiliate.id)}
                        onCheckedChange={(checked) =>
                          handleAffiliateToggle(affiliate.id, checked as boolean)
                        }
                        disabled={uploading}
                      />
                      <Label 
                        htmlFor={`affiliate-${affiliate.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {affiliate.name} ({affiliate.email})
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={uploading || !file || !name.trim() || !displayType}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Asset
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadPromoAssetModal;
